/**
 * 列主元 Householder QR 秩揭示分析。
 *
 * 文件职责：
 * 1. 回答“新增方程是否独立”“剩余自由度是多少”——迭代收敛与否无法区分
 *    冗余与冲突，必须做秩分析（02 计划 §7.2）；
 * 2. 纯数值、无几何语义；语义在阶段 0 原型 `SketchPrototypeRankAnalyzer` 验证，
 *    本模块为其生产迁移；
 * 3. 秩阈值使用相对 + 绝对双下限：几何约束系统的秩间隙远大于数值微分误差（约 1e-10）。
 */

/** 矩阵秩分析结果。 */
export interface MatrixRankAnalysis {
    /** 数值秩。 */
    readonly rank: number;
    /** 列数（自由参数个数）。 */
    readonly columnCount: number;
    /** 行数（残差行总数）。 */
    readonly rowCount: number;
    /** 剩余自由度 = 列数 - 秩。 */
    readonly degreesOfFreedom: number;
    /** QR 对角绝对值序列（随列主元自然降序），用于诊断秩判定边界。 */
    readonly diagonal: readonly number[];
}

/** 秩分析选项。 */
export interface MatrixRankAnalysisOptions {
    /** 相对秩阈值：|R_kk| > relativeTolerance * |R_00| 视为有效秩；默认 1e-7。 */
    readonly relativeTolerance?: number;
    /** 绝对秩下限，防止全零矩阵误报；默认 1e-10。 */
    readonly absoluteFloor?: number;
}

/**
 * 执行列主元 Householder QR，返回 R 对角绝对值序列。
 *
 * @param matrix 输入矩阵（行 × 列）。
 * @param columnCount 列数。
 * @param absoluteFloor 退化判定绝对下限。
 * @returns |R_kk| 序列。
 */
function computeQrDiagonal(
    matrix: readonly (readonly number[])[],
    columnCount: number,
    absoluteFloor: number,
): number[] {
    const rowCount = matrix.length;
    if (rowCount === 0 || columnCount === 0) {
        return [];
    }

    // 工作副本：work[row][col]。
    const work = matrix.map((row) => [...row]);
    const stepCount = Math.min(rowCount, columnCount);
    const diagonal: number[] = [];

    for (let step = 0; step < stepCount; step += 1) {
        // 每步全量重算尾部列范数，规避递推更新的数值漂移。
        // ponytail: O(m·n²) 全量范数扫描，island 参数规模（几十列）下开销可忽略；
        // 若未来大 island 秩分析成为热点，升级为递推列范数 + 周期性重算。
        let pivotColumn = step;
        let pivotNorm = -1;
        for (let column = step; column < columnCount; column += 1) {
            let normSquared = 0;
            for (let row = step; row < rowCount; row += 1) {
                const entry = work[row]![column]!;
                normSquared += entry * entry;
            }
            if (normSquared > pivotNorm) {
                pivotNorm = normSquared;
                pivotColumn = column;
            }
        }
        if (pivotNorm <= absoluteFloor * absoluteFloor) {
            // 剩余列全部退化，秩到此为止。
            break;
        }
        // 列交换（列主元）。
        if (pivotColumn !== step) {
            for (let row = 0; row < rowCount; row += 1) {
                const swap = work[row]![step]!;
                work[row]![step] = work[row]![pivotColumn]!;
                work[row]![pivotColumn] = swap;
            }
        }

        // 构造第 step 列的 Householder 反射向量。
        let columnNorm = 0;
        for (let row = step; row < rowCount; row += 1) {
            columnNorm += work[row]![step]! * work[row]![step]!;
        }
        columnNorm = Math.sqrt(columnNorm);
        const head = work[step]![step]!;
        const alpha = head >= 0 ? -columnNorm : columnNorm;
        const householder: number[] = [];
        for (let row = step; row < rowCount; row += 1) {
            householder.push(work[row]![step]!);
        }
        householder[0] = householder[0]! - alpha;
        let householderNormSquared = 0;
        householder.forEach((value) => {
            householderNormSquared += value * value;
        });

        if (householderNormSquared > absoluteFloor * absoluteFloor) {
            // 对尾部子矩阵应用反射：A -= 2 v (vᵀA) / (vᵀv)。
            for (let column = step; column < columnCount; column += 1) {
                let projection = 0;
                for (let row = step; row < rowCount; row += 1) {
                    projection += householder[row - step]! * work[row]![column]!;
                }
                const scale = (2 * projection) / householderNormSquared;
                for (let row = step; row < rowCount; row += 1) {
                    work[row]![column] = work[row]![column]! - scale * householder[row - step]!;
                }
            }
        }

        diagonal.push(Math.abs(work[step]![step]!));
    }

    return diagonal;
}

/**
 * 分析矩阵数值秩与自由度。
 *
 * @param matrix 输入矩阵（行=残差，列=自由参数）。
 * @param columnCount 列数（自由参数个数）。
 * @param options 秩分析选项。
 * @returns 秩分析结果。
 */
export function analyzeMatrixRank(
    matrix: readonly (readonly number[])[],
    columnCount: number,
    options?: MatrixRankAnalysisOptions,
): MatrixRankAnalysis {
    const relativeTolerance = options?.relativeTolerance ?? 1e-7;
    const absoluteFloor = options?.absoluteFloor ?? 1e-10;
    const diagonal = computeQrDiagonal(matrix, columnCount, absoluteFloor);
    const threshold = Math.max((diagonal[0] ?? 0) * relativeTolerance, absoluteFloor);
    const rank = diagonal.filter((value) => value > threshold).length;

    return {
        rank,
        columnCount,
        rowCount: matrix.length,
        degreesOfFreedom: columnCount - rank,
        diagonal,
    };
}
